import React from 'react';
import './ScoreCard.css';
import { playMeowSurprise, playPurrContent, playMeowHappy } from '../utils/audioUtils';

const ScoreCard = ({ 
  title, 
  active, 
  scores, 
  dice, 
  onScore, 
  calculateScore, 
  totalScore, 
  rollsLeft,
  playerNumber 
}) => {
  // Score categories
  const upperSectionCategories = [
    { id: 'ones', name: 'Ones' },
    { id: 'twos', name: 'Twos' },
    { id: 'threes', name: 'Threes' },
    { id: 'fours', name: 'Fours' },
    { id: 'fives', name: 'Fives' },
    { id: 'sixes', name: 'Sixes' },
  ];

  const lowerSectionCategories = [
    { id: 'threeOfAKind', name: 'Three of a Kind' },
    { id: 'fourOfAKind', name: 'Four of a Kind' },
    { id: 'fullHouse', name: 'Full House' },
    { id: 'smallStraight', name: 'Small Straight' },
    { id: 'largeStraight', name: 'Large Straight' },
    { id: 'yahtzee', name: 'CATZEE' },
    { id: 'chance', name: 'Chance' }
  ];

  // Calculate upper section subtotal
  const upperSectionTotal = upperSectionCategories
    .reduce((sum, category) => sum + (scores[category.id] || 0), 0);
  
  // Check if upper section bonus applies (63 or more points)
  const upperSectionBonus = upperSectionTotal >= 63 ? 35 : 0;

  const handleCategoryClick = (categoryId) => {
    const isScored = scores[categoryId] !== undefined;
    const isClickable = active && !isScored && rollsLeft !== 3;
    
    if (isClickable) {
      // Play surprise meow when scoring
      playMeowSurprise();
      onScore(categoryId);
    } else if (active && rollsLeft === 3) {
      // Play content purr when clicking before rolling
      playPurrContent();
    } else if (isScored) {
      // Play happy meow when clicking already scored category
      playMeowHappy();
    }
  };

  // Render a score category row
  const renderCategory = (category) => {
    const isScored = scores[category.id] !== undefined;
    const potentialScore = isScored ? null : calculateScore(category.id);
    const isClickable = active && !isScored && rollsLeft !== 3;

    return (
      <tr 
        key={category.id} 
        className={`score-row ${isClickable ? 'clickable' : ''} ${isScored ? 'scored' : ''}`}
        onClick={() => handleCategoryClick(category.id)}
        style={{ cursor: 'pointer' }}
      >
        <td className="category-name">
          <span className="name">
            {category.name}
          </span>
        </td>
        <td className="score-value">
          {isScored ? (
            scores[category.id]
          ) : (
            active && rollsLeft !== 3 ? (
              <span className="potential-score">{potentialScore}</span>
            ) : null
          )}
        </td>
      </tr>
    );
  };

  // Get cat avatar image for the player
  const catAvatars = [
    '/cat-images/cat-high-5.png',
    '/cat-images/cat-surprise.png'
  ];

  return (
    <div className={`score-card ${active ? 'active' : ''}`}>
      <div className="score-card-header">
        <img 
          src={catAvatars[playerNumber - 1]} 
          alt={`Player ${playerNumber} cat avatar`}
          className="player-cat-avatar"
        />
        <div className="player-info">
          <h2>{title}</h2>
          <div className="score-display">Score: {totalScore}</div>
        </div>
      </div>
      
      <div className="score-tables-container">
        <table className="score-table">
          <thead>
            <tr>
              <th colSpan="2">Upper Section</th>
            </tr>
          </thead>
          <tbody>
            {upperSectionCategories.map(category => renderCategory(category))}
            <tr className="subtotal-row">
              <td>Subtotal</td>
              <td>{upperSectionTotal}</td>
            </tr>
            <tr className="bonus-row">
              <td>Bonus (if ≥ 63)</td>
              <td>{upperSectionBonus}</td>
            </tr>
          </tbody>
        </table>
        
        <table className="score-table">
          <thead>
            <tr>
              <th colSpan="2">Lower Section</th>
            </tr>
          </thead>
          <tbody>
            {lowerSectionCategories.map(category => renderCategory(category))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScoreCard;